import { getJsNow } from 'utils/date';
import * as yup from 'yup';
import _mapValues from 'lodash/mapValues';
import _keyBy from 'lodash/keyBy';
import * as dbApi from 'resources/db/db.api';
import process from 'utils/processResponse';

class DbService {
  constructor(tableName, options) {
    this.tableName = tableName;
    this.options = options || {};
    if (!options.schema) {
      throw new Error(`Please, provide schema for [${tableName}]`);
    }
  }

  processResponse(response) {
    return process(response);
  }

  getPartialSchema(partialUpdate) {
    const updateKeys = Object.keys(partialUpdate);

    const schemaShapeDef = _mapValues(_keyBy(updateKeys), (path) => yup.reach(this.options.schema, path));

    return yup.object().shape(schemaShapeDef);
  }

  async validateSchema(entity, alternateSchema) {
    const schema = alternateSchema || this.options.schema;
    try {
      return schema.validate(entity);
    } catch (err) {
      console.error(`Schema is not valid for ${this.tableName} collection: ${err.stack || err}`, entity);
      throw err;
    }
  }

  async create(object) {
    const now = getJsNow();

    const entity = {
      ...object,
      created_on: now,
      updated_on: now,
    };
    const validatedEntity = await this.validateSchema(entity);

    const response = await dbApi.insertOne({
      tableName: this.tableName,
      entity: validatedEntity,
    });

    return this.processResponse(response);
  }

  async createMany(objects) {
    const entities = (Array.isArray(objects) ? objects : [objects])
      .map((object) => ({
        ...object,
        created_on: getJsNow(),
        updated_on: getJsNow(),
      }));

    const response = await dbApi.insertMany({
      tableName: this.tableName,
      entities,
    });

    return this.processResponse(response);
  }

  async updateOne(matchQuery, partialUpdate) {
    const update = {
      ...partialUpdate,
      updated_on: getJsNow(),
    };

    const partialSchema = this.getPartialSchema(update);
    const validatedUpdate = await this.validateSchema(update, partialSchema);

    const response = await dbApi.updateOne({
      tableName: this.tableName,
      matchQuery,
      validatedUpdate,
    });

    return this.processResponse(response);
  }

  async selectByKey({ selectKey, params }) {
    const response = await dbApi.select({
      selectKey,
      params,
    });

    return this.processResponse(response);
  }

  async publicSelectByKey({ selectKey, params }) {
    const response = await dbApi.publicSelect({
      selectKey,
      params,
    });

    return this.processResponse(response);
  }

  /**
   * @param {Record<string, any>} matchQuery
   * @param {{ column: string; value: any }[]} isQuery
   * @returns {Promise<any[]>}
   */
  async find(matchQuery, isQuery = []) {
    let response = await dbApi.find({
      tableName: this.tableName,
      matchQuery,
      isQuery,
    });

    response = this.processResponse(response);

    return response.data.length > 0 ? response.data : null;
  }

  async findById(id) {
    if (!id) return null;

    const data = await this.find({ id });
    return data?.[0] || null;
  }

  async remove(matchQuery) {
    const response = await dbApi.remove({
      tableName: this.tableName,
      matchQuery,
    });

    return this.processResponse(response);
  }
}

export default DbService;
